import React, { useState } from 'react';
import { signInWithPassword, sendMagicLinkEmail } from './lib/supabaseAuth';
import { Link } from 'react-router-dom'; // 🔥 1. ضفنا استيراد اللينك
import { useI18n } from './i18n';

// استيراد الصور من مجلد assets
import dafagencyLogo from './assets/dafagency_logo.png';
import loginBackground from './assets/background_login.jpg';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useI18n();

  // 🔗 "Sign in with a magic link" — added for merchants whose Daf
  // Confirmation account was auto-provisioned by the Daf One bridge
  // (confirmation-provision.js) and never got a password they know. Kept
  // as a completely separate mini-form (not touching the password path
  // above at all) so independent merchants signing in with a password are
  // zero-affected either way.
  const [magicMode, setMagicMode] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Replaces Firebase's signInWithEmailAndPassword(auth, email, password)
      // — GoTrue password-grant login (see src/lib/supabaseAuth.js).
      const { error: authError } = await signInWithPassword(email, password);
      if (authError) throw authError;
    } catch (err) {
      console.error("Login Error:", err.status, err.message);
      // GoTrue doesn't use Firebase-style error codes (auth/wrong-password,
      // etc.) — it returns a plain message + HTTP status. Map the common
      // ones to the same Arabic copy the old Firebase branches used.
      const message = String(err.message || '');
      if (/invalid login credentials/i.test(message) || /invalid email/i.test(message)) {
          setError("الإيميل أو الباسورد غلط، حاول تاني.");
      } else if (err.status === 429 || /rate limit/i.test(message)) {
          setError("محاولات كتير غلط، استنى شوية وحاول تاني.");
      } else {
          setError("حصل خطأ غير متوقع، حاول تاني.");
      }
    } finally {
        setLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    setMagicError(null);
    setMagicSending(true);
    try {
      const { error: sendError } = await sendMagicLinkEmail(magicEmail);
      if (sendError) throw sendError;
      setMagicSent(true);
    } catch (err) {
      console.error('Magic-link request error:', err);
      setMagicError(t('login.magicLink.error'));
    } finally {
      setMagicSending(false);
    }
  };

  return (
    <div style={loginStyles.backgroundContainer}>
      <div style={loginStyles.overlay}></div>
      <div style={loginStyles.loginCard}>
        <img src={dafagencyLogo} alt="Dafagency.ai Logo" style={loginStyles.logo} />

        <h2 style={{ marginBottom: '30px', fontWeight: 'normal' }}>{t('login.title')}</h2>

        {!magicMode ? (
          <>
            <form onSubmit={handleLogin}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.email')}
                required
                style={loginStyles.input}
                disabled={loading}
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.password')}
                required
                style={loginStyles.input}
                disabled={loading}
              />
              <button type="submit" style={loginStyles.button} disabled={loading}>
                {loading ? t('login.loading') : t('login.submit')}
              </button>
              {error && <p style={loginStyles.errorText}>{error}</p>}
            </form>

            {/* 🔗 Magic-link toggle — for merchants auto-provisioned via the
                Daf One bridge who have no password. Does not touch the
                password form above at all. */}
            <div style={{ marginTop: '14px' }}>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>{t('login.or')}</span>
              <button
                type="button"
                onClick={() => { setMagicMode(true); setMagicSent(false); setMagicError(null); }}
                style={loginStyles.magicToggleButton}
              >
                🔗 {t('login.magicLink.toggle')}
              </button>
            </div>
          </>
        ) : (
          <div>
            {!magicSent ? (
              <form onSubmit={handleMagicLinkRequest}>
                <input
                  type="email"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  placeholder={t('login.magicLink.email')}
                  required
                  style={loginStyles.input}
                  disabled={magicSending}
                />
                <button type="submit" style={loginStyles.button} disabled={magicSending}>
                  {magicSending ? t('login.magicLink.sending') : t('login.magicLink.submit')}
                </button>
                {magicError && <p style={loginStyles.errorText}>{magicError}</p>}
              </form>
            ) : (
              <p style={{ color: '#7CE7A0', fontSize: '14px', lineHeight: 1.6 }}>
                {t('login.magicLink.sent')}
              </p>
            )}
            <button
              type="button"
              onClick={() => { setMagicMode(false); setMagicSent(false); setMagicError(null); }}
              style={{ ...loginStyles.magicToggleButton, marginTop: '10px' }}
            >
              ← {t('login.magicLink.toggleBack')}
            </button>
          </div>
        )}

        {/* 🔥 2. ضفنا برجراف التسجيل الجديد هنا */}
        <p style={loginStyles.signupText}>
          {t('login.signupPrompt')}{' '}
          <Link to="/signup" style={loginStyles.signupLink}>
            {t('login.signupCta')}
          </Link>
        </p>

      </div>
    </div>
  );
}

// الستايلات الجديدة لشاشة اللوجن
const loginStyles = {
  backgroundContainer: {
    backgroundImage: `url(${loginBackground})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1,
  },
  loginCard: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(15px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '15px',
    padding: '40px 30px',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center',
    color: 'white',
  },
  logo: {
    width: '130px',
    height: 'auto',
    marginBottom: '25px',
  },
  input: {
    display: 'block',
    width: 'calc(100% - 22px)',
    padding: '12px 10px',
    marginBottom: '15px',
    fontSize: '16px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '5px',
    color: 'white',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.3s ease',
  },
  button: {
    display: 'block',
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    fontWeight: 'bold',
    background: '#00BFFF',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginTop: '10px',
    transition: 'background-color 0.3s ease',
  },
  errorText: {
    color: '#ff8a80',
    textAlign: 'center',
    marginTop: '15px',
    fontSize: '14px',
  },
  // 🔥 3. ضفنا الستايلات دي للينك الجديد
  signupText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '14px',
    marginTop: '20px',
  },
  signupLink: {
    color: '#00BFFF', // نفس لون الزرار
    textDecoration: 'underline',
    fontWeight: 'bold',
  },
  // 🔗 Magic-link toggle button — plain text-link look, distinct from the
  // primary submit button so it reads as a secondary action.
  magicToggleButton: {
    display: 'block',
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#00BFFF',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '8px',
    padding: '6px',
    textAlign: 'center',
  },
};

export default LoginPage;